/**
 * carnival.js
 * ---------------------------------------------------------------------------
 * Elfte Schwesterdatei der Reihe: identische Logik, eigener Namensraum (ca-).
 *
 * Erzeugt die Masken und Narrenkappen im Hintergrund und lässt sie umherziehen.
 * Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius Pixel, stieben
 * sie auseinander und beruhigen sich danach wieder auf ihr Reisetempo.
 * Konfetti, Wimpelketten und Lauflicht laufen rein über CSS.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Mond und Straßenzug.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initCarnival } from "./carnival.js";
 *   initCarnival();
 *
 * Aktiviert wird jedes Element mit der Klasse .ca-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-carnival.
 */
export function carnival() {
    "use strict";

    var CONFIG = {
        masks:      5,       // Anzahl Masken (Desktop, mobil halbiert)
        caps:       6,       // Anzahl Narrenkappen
        fleeRadius: 170,     // ab dieser Distanz zum Zeiger weichen sie aus (px)
        fleeForce:  1.1,     // Heftigkeit des Ausweichens
        maxSpeed:   3.6,     // Fluchttempo (px pro Frame bei 60 fps)
        maskSpeed:  0.7,     // Reisetempo Maske
        capSpeed:   0.32,    // Reisetempo Narrenkappe
        wander:     0.07,    // Richtungswechsel: hoch, hier taumelt alles
        calmDown:   0.012,   // wie schnell der Schreck abklingt
        margin:     70,      // ab diesem Randabstand drehen sie ein
        maskSize:   [20, 36],
        capSize:    [16, 30]
    };

    var COLORS = ["#e0407a", "#f0b429", "#2fb8c6", "#8ad14a", "#a765d6", "#ef6a3d"];

    var MASK =
        '<span class="ca-critter__body">' +
        '<i class="ca-ribbon ca-ribbon--l"></i>' +
        '<i class="ca-ribbon ca-ribbon--r"></i>' +
        '<i class="ca-mask"></i>' +
        '</span>';

    var CAP = '<span class="ca-critter__body"><i></i><i></i><i></i><i></i></span>';

    function reducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function rand(min, max) { return min + Math.random() * (max - min); }
    function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

    function create(scene) {
        var layer = scene.querySelector(".ca-critters");
        if (!layer) return;

        var W = window.innerWidth;
        var H = window.innerHeight;
        var small = W < 700;
        var critters = [];
        var pointer = { x: -9999, y: -9999, active: false };
        var running = true;
        var last = 0;

        function spawn(kind, count) {
            var isMask = kind === "mask";
            var range = isMask ? CONFIG.maskSize : CONFIG.capSize;

            for (var i = 0; i < count; i++) {
                var size = rand(range[0], range[1]);
                var el = document.createElement("span");
                el.className = "ca-critter ca-critter--" + kind;
                el.innerHTML = isMask ? MASK : CAP;
                el.style.setProperty("--ca-size", size.toFixed(1) + "px");
                el.style.setProperty("--ca-color", pick(COLORS));
                el.style.opacity = rand(0.82, 1).toFixed(2);
                if (!isMask) {
                    el.querySelector(".ca-critter__body").style.animationDelay =
                        (-rand(0, 3.4)).toFixed(2) + "s";
                }
                layer.appendChild(el);

                var dir = rand(0, Math.PI * 2);
                var base = (isMask ? CONFIG.maskSpeed : CONFIG.capSpeed) * rand(0.75, 1.3);

                critters.push({
                    el: el,
                    kind: kind,
                    w: size,
                    h: isMask ? size / 1.6 : size,
                    x: rand(0, W),
                    y: rand(0, H * 0.8),
                    vx: Math.cos(dir) * base,
                    vy: Math.sin(dir) * base,
                    base: base,
                    flee: 0,
                    face: 1,
                    fleeing: false
                });
            }
        }

        spawn("mask", small ? Math.ceil(CONFIG.masks / 2) : CONFIG.masks);
        spawn("cap", small ? Math.ceil(CONFIG.caps / 2) : CONFIG.caps);

        function step(now) {
            if (!running) return;
            var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
            last = now;

            for (var i = 0; i < critters.length; i++) {
                var c = critters[i];

                c.vx += rand(-1, 1) * CONFIG.wander * dt;
                c.vy += rand(-1, 1) * CONFIG.wander * dt;

                if (pointer.active) {
                    var dx = c.x - pointer.x;
                    var dy = c.y - pointer.y;
                    var d = Math.sqrt(dx * dx + dy * dy);
                    if (d < CONFIG.fleeRadius) {
                        var push = 1 - d / CONFIG.fleeRadius;
                        var inv = 1 / (d || 1);
                        c.vx += dx * inv * CONFIG.fleeForce * push * dt;
                        c.vy += dy * inv * CONFIG.fleeForce * push * dt;
                        c.flee = 1;
                    }
                }
                c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

                if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
                else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
                if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
                else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

                var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
                var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
                var next = speed + (target - speed) * 0.05 * dt;
                c.vx = c.vx / speed * next;
                c.vy = c.vy / speed * next;

                c.x += c.vx * dt;
                c.y += c.vy * dt;
                if (c.x < -c.w) c.x = W + c.w;
                else if (c.x > W + c.w) c.x = -c.w;
                if (c.y < -c.h) c.y = H + c.h;
                else if (c.y > H + c.h) c.y = -c.h;

                c.el.style.transform =
                    "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
                    (c.y - c.h / 2).toFixed(1) + "px,0)";

                if (c.kind === "mask") {
                    var face = c.vx < 0 ? -1 : 1;
                    if (face !== c.face) {
                        c.face = face;
                        c.el.style.setProperty("--ca-face", face);
                    }
                }

                var fleeing = c.flee > 0.2;
                if (fleeing !== c.fleeing) {
                    c.fleeing = fleeing;
                    c.el.classList.toggle("is-fleeing", fleeing);
                }
            }

            requestAnimationFrame(step);
        }

        window.addEventListener("pointermove", function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
        }, { passive: true });

        document.addEventListener("pointerleave", function () {
            pointer.active = false;
        });

        window.addEventListener("resize", function () {
            W = window.innerWidth;
            H = window.innerHeight;
        }, { passive: true });

        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                running = false;
            } else if (!running) {
                running = true;
                last = 0;
                requestAnimationFrame(step);
            }
        });

        requestAnimationFrame(step);
    }

    function init(scene) {
        if (scene.dataset.caReady) return;
        scene.dataset.caReady = "1";

        window.addEventListener("pointermove", function (e) {
            scene.style.setProperty("--ca-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
            scene.style.setProperty("--ca-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
        }, { passive: true });

        if (!reducedMotion()) create(scene);
    }

    function boot() {
        var scenes = document.querySelectorAll(".ca-scene");
        if (!scenes.length) return;

        document.body.classList.add("is-carnival");

        Array.prototype.forEach.call(scenes, init);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
}